// frontend/src/presentation/hooks/useAiWalkieTalkie.ts
//
// Hook asystenta AI "Woki-Toki" — warstwa prezentacji. Domyka pętlę na froncie:
//
//   1. `dispatchAiQuery(query)` kolejkuje pytanie jako `ai_query/create`
//      w lokalnej kolejce Dexie (`SyncQueueRepository`) i ustawia `status`
//      na `'pending'` (UI może od razu pokazać „asystent myśli…"),
//   2. rejestruje JEDNORAZOWY nasłuch odpowiedzi dla wygenerowanego `messageId`
//      w kliencie WebSocket (`AiWebSocketClient`, singleton),
//   3. wyzwala synchronizację (`useNetworkSync().triggerSync`), która wypycha
//      wpis na `POST /api/sync`,
//   4. gdy backend odeśle `{ type: 'AI_RESPONSE', messageId, answer }`, hook
//      czyta odpowiedź na głos (`SpeechSynthesisService`), zapamiętuje ją w
//      `lastAnswer` (`status` → `'answered'`) i odpina nasłuch — dzięki temu
//      ekran może pokazać treść odpowiedzi, a nie tylko ją wypowiedzieć.
//
// To NOWY tor (Clean Architecture) — nie modyfikuje starego dyktowania notatek.

import { useCallback, useState } from 'react'

import { db } from '../../infrastructure/offline/AppDatabase'
import {
  SyncQueueRepository,
  type ISyncQueueRepository,
} from '../../infrastructure/repositories/SyncQueueRepository'
import {
  getAiWebSocketClient,
  type AiWebSocketClient,
} from '../../infrastructure/websocket/AiWebSocketClient'
import {
  speechSynthesisService,
  type SpeechSynthesisService,
} from '../services/SpeechSynthesisService'
import { useNetworkSyncContext } from '../context/networkSync'

/** Typ encji kolejki synchronizacji dla zapytań asystenta AI. */
export const AI_QUERY_ENTITY = 'ai_query'
/** Akcja kolejki synchronizacji dla zapytań asystenta AI. */
export const AI_QUERY_ACTION = 'create'

/** Ładunek operacji `ai_query/create` — kształt oczekiwany przez backend (SyncController). */
export interface AiQueryPayload {
  messageId: string
  query: string
}

/** Zależności hooka — domyślnie produkcyjne singletony; w testach podstawiamy atrapy. */
export interface UseAiWalkieTalkieDeps {
  repo?: ISyncQueueRepository
  wsClient?: AiWebSocketClient
  speech?: SpeechSynthesisService
  generateId?: () => string
}

/** Stan bieżącego zapytania do asystenta AI — steruje banerem w UI (patrz `Screen1`). */
export type AiWalkieTalkieStatus = 'idle' | 'pending' | 'answered'

export interface AiWalkieTalkie {
  /** Stan sieci z silnika synchronizacji (do UI / diagnostyki). */
  isOnline: boolean
  /**
   * Stan ostatniego zapytania: `'idle'` — brak/skasowane, `'pending'` — czeka
   * na odpowiedź (zakolejkowano, WS jeszcze nic nie odesłał), `'answered'` —
   * `lastAnswer` zawiera gotową treść.
   */
  status: AiWalkieTalkieStatus
  /** Treść ostatniej odpowiedzi asystenta (ta sama, którą przeczytano na głos) albo `null`. */
  lastAnswer: string | null
  /**
   * Wysyła zapytanie do asystenta AI: kolejkuje `ai_query/create`, rejestruje
   * jednorazowy nasłuch odpowiedzi (czyta ją na głos i zapisuje w `lastAnswer`)
   * i wyzwala synchronizację.
   *
   * @returns wygenerowany `messageId` skojarzony z zapytaniem albo `null`,
   *   gdy `query` po przycięciu białych znaków jest puste.
   */
  dispatchAiQuery: (query: string) => string | null
  /** Zeruje `status`/`lastAnswer` (np. ekran chce ukryć baner albo startuje nowe dyktowanie). */
  clearAnswer: () => void
}

/** Współdzielone repozytorium kolejki (singleton bazy `db`) — jedno na aplikację. */
const defaultRepo: ISyncQueueRepository = new SyncQueueRepository(db)

function defaultGenerateId(): string {
  return crypto.randomUUID()
}

export function useAiWalkieTalkie(deps: UseAiWalkieTalkieDeps = {}): AiWalkieTalkie {
  const { isOnline, triggerSync } = useNetworkSyncContext()

  const repo = deps.repo ?? defaultRepo
  const speech = deps.speech ?? speechSynthesisService
  const generateId = deps.generateId ?? defaultGenerateId
  const wsClient = deps.wsClient ?? getAiWebSocketClient()

  const [status, setStatus] = useState<AiWalkieTalkieStatus>('idle')
  const [lastAnswer, setLastAnswer] = useState<string | null>(null)

  const clearAnswer = useCallback((): void => {
    setStatus('idle')
    setLastAnswer(null)
  }, [])

  const dispatchAiQuery = useCallback(
    (query: string): string | null => {
      const trimmed = query.trim()
      if (!trimmed) return null

      const messageId = generateId()

      // Sygnał dla UI od razu, zanim zakolejkowanie/sync w ogóle się zakończy —
      // ekran ma pokazać „asystent myśli…" bez czekania na sieć.
      setLastAnswer(null)
      setStatus('pending')

      // Jednorazowy nasłuch dokładnie tej odpowiedzi — po odczytaniu odpina się sam.
      const unsubscribe = wsClient.onAiResponse((incomingId, answer) => {
        if (incomingId !== messageId) return
        unsubscribe()
        speech.speak(answer)
        setLastAnswer(answer)
        setStatus('answered')
      })

      const payload: AiQueryPayload = { messageId, query: trimmed }

      // Enqueue + sync w tle (fire-and-forget) — spójnie z IncidentSummaryScreen.
      // Sieć nieobowiązkowa: offline wpis czeka w kolejce, a `useNetworkSync`
      // dokończy wysyłkę po zdarzeniu 'online'.
      repo
        .enqueue(AI_QUERY_ENTITY, AI_QUERY_ACTION, payload)
        .then(() => {
          void triggerSync()
        })
        .catch((error: unknown) => {
          // Nie udało się nawet zakolejkować — nasłuch nie ma czego czekać,
          // a UI nie może utknąć w wiecznym „pending".
          unsubscribe()
          setStatus('idle')
          console.error(
            '[useAiWalkieTalkie] Nie udało się zakolejkować zapytania AI.',
            error,
          )
        })

      return messageId
    },
    [repo, speech, generateId, wsClient, triggerSync],
  )

  return { isOnline, status, lastAnswer, dispatchAiQuery, clearAnswer }
}
